import React, { useCallback, useEffect, useState } from 'react';
import {
  apiErrorMessage,
  getAdminOverview,
  issueInvitation,
  revokeInvitation,
  shareInvitation,
  updateInvitation,
} from '../api/adpilotApi';
import ReferralFunnel from './ReferralFunnel';
import { channelFor } from '../channels';

const number = new Intl.NumberFormat('en-US');
const relativeTime = (value) => (value ? new Date(value).toLocaleString() : 'Never');
const channelLabel = (provider) => channelFor(provider).label;
const userChannels = (user) => (Array.isArray(user.channels) && user.channels.length
  ? user.channels
  : [{ provider: 'facebook', ad_account_id: user.ad_account_id, status: user.connection_status }]);
const displayValue = (value, field = '', currency = 'USD') => (/_micros$/.test(field) && Number.isFinite(Number(value))
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value) / 1_000_000)
  : Array.isArray(value)
  ? value.map((item) => displayValue(item, field, currency)).join(', ')
  : value && typeof value === 'object'
    ? Object.entries(value).filter(([, item]) => item != null && item !== '')
      .map(([key, item]) => `${key.replaceAll('_', ' ')}: ${displayValue(item, key, currency)}`).join('; ')
    : String(value));
const actionSuggestion = (action) => {
  const requested = action.requested_state;
  if (!requested || typeof requested !== 'object') return null;
  const values = requested.suggested_values && typeof requested.suggested_values === 'object'
    ? requested.suggested_values
    : requested;
  if (Number.isFinite(Number(values.current_budget_cents)) && Number.isFinite(Number(values.new_budget_cents))) {
    return `Suggested: $${(Number(values.current_budget_cents) / 100).toFixed(2)} → $${(Number(values.new_budget_cents) / 100).toFixed(2)}`;
  }
  const proposed = values.recommended_value ?? values.suggested_values ?? values.status;
  return proposed == null ? null : `Suggested: ${displayValue(proposed, '', values.account_currency || 'USD')}`;
};

function AdminPage() {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [issuedInvitation, setIssuedInvitation] = useState(null);
  const [sharedLink, setSharedLink] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await getAdminOverview();
      setOverview(result);
      setError(null);
    } catch (loadError) {
      setError(apiErrorMessage(loadError, 'Failed to load product activity'));
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const handleIssue = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await issueInvitation({ label, expiresInDays });
      setIssuedInvitation(result.invitation);
      setSharedLink(null);
      setCopiedId(null);
      setLabel('');
      await load();
    } catch (issueError) {
      setError(apiErrorMessage(issueError, 'Failed to issue invitation'));
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async (invitation) => {
    setBusy(true);
    setError(null);
    try {
      const result = await shareInvitation(invitation.id);
      const link = `${window.location.origin}/login?invite=${encodeURIComponent(result.share_token)}`;
      setSharedLink({ id: invitation.id, link });
      setIssuedInvitation(null);
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(link);
          setCopiedId(invitation.id);
        } catch (copyError) {
          setError('The invitation is marked Shared. Copy the link shown below manually.');
        }
      }
      await load();
    } catch (shareError) {
      setError(apiErrorMessage(shareError, 'Failed to copy invitation link'));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await updateInvitation(editing);
      setEditing(null);
      await load();
    } catch (updateError) {
      setError(apiErrorMessage(updateError, 'Failed to update invitation'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (invitation) => {
    if (!window.confirm(`Revoke the invitation for ${invitation.label}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await revokeInvitation(invitation.id);
      await load();
    } catch (revokeError) {
      setError(apiErrorMessage(revokeError, 'Failed to revoke invitation'));
    } finally {
      setBusy(false);
    }
  };

  const metrics = overview?.metrics || {};
  const users = overview?.users || [];
  const actions = overview?.actions || [];
  const invitations = overview?.invitations || [];
  const funnel = overview?.referral_funnel || {};
  const sendableInvitations = invitations.filter((item) => ['ready', 'shared', 'opened'].includes(item.status));

  return (
    <div className="page admin-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Internal admin</p>
          <h1 className="page-title">Product pulse.</h1>
          <p className="page-subtitle">Active AdPilot users, agent execution, and approved Meta changes.</p>
        </div>
        {!overview && !error
          ? <span className="agent-online agent-online-loading" role="status"><span className="spinner" aria-hidden="true" />Loading product pulse</span>
          : <span className="agent-online"><i /> Refreshes every 30 seconds</span>}
      </header>

      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {!overview && !error && <section className="page-loading" aria-busy="true" aria-label="Loading product activity"><div className="page-loading-metrics page-loading-metrics-four">{[1, 2, 3, 4].map((item) => <span key={item} />)}</div><div className="card page-loading-card"><span className="loading-line loading-line-short" /><span className="loading-line" /><span className="loading-block" /></div></section>}

      {overview && (
        <>
          <section className="admin-metrics" aria-label="Key metrics">
            <div><strong>{number.format(metrics.active_users_7d || 0)}</strong><span>Active users · 7d</span></div>
            <div><strong>{number.format(metrics.connected_accounts || 0)}</strong><span>Connected accounts</span></div>
            <div><strong>{number.format(metrics.agent_runs_7d || 0)}</strong><span>Agent runs · 7d</span></div>
            <div><strong>{number.format(metrics.changes_applied_7d || 0)}</strong><span>Changes applied · 7d</span></div>
          </section>

          <section className="card admin-panel">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Referral funnel</h2><span>From shared link to 30-day activity</span></div>
            </div>
            <ReferralFunnel metrics={funnel} label="All-user referral funnel" />
          </section>

          <section className="card admin-panel">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Invitation access</h2><span>Ready means never copied · Opened confirms recipient activity</span></div>
              <span>{invitations.filter((item) => item.status === 'ready').length} ready · {sendableInvitations.length} can send</span>
            </div>
            <form className="invite-form" onSubmit={handleIssue}>
              <label><span>Who is this for?</span><input value={label} onChange={(event) => setLabel(event.target.value)} maxLength="120" placeholder="Dave's Garage or August beta" required /></label>
              <label><span>Expires</span><select value={expiresInDays} onChange={(event) => setExpiresInDays(Number(event.target.value))}><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select></label>
              <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create invitation'}</button>
            </form>
            {issuedInvitation && (
              <div className="invite-secret" role="status">
                <div><strong>{issuedInvitation.label} is ready</strong><small>Copying records this invitation as Copied. You can copy it again until it is redeemed or expires.</small></div>
                <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => handleShare(issuedInvitation)}>Copy link</button>
              </div>
            )}
            {sharedLink && <div className="invite-share-link" role="status"><code>{sharedLink.link}</code><span>{copiedId === sharedLink.id ? 'Copied' : 'Ready to copy manually'}</span></div>}
            <div className="invite-list">
              {invitations.map((item) => (
                <div className="invite-row" key={item.id}>
                  {editing?.id === item.id ? (
                    <form className="invite-edit" onSubmit={handleUpdate}>
                      <div className="invite-edit-context">
                        <strong>Edit invitation</strong>
                        <small>Update the label or extend access from today.</small>
                      </div>
                      <label>
                        <span>Invitation label</span>
                        <input aria-label="Invitation label" value={editing.label} maxLength="120" required onChange={(event) => setEditing({ ...editing, label: event.target.value })} />
                      </label>
                      <label>
                        <span>New expiration</span>
                        <select aria-label="Extend expiration" value={editing.expiresInDays} onChange={(event) => setEditing({ ...editing, expiresInDays: Number(event.target.value) })}><option value="7">7 days</option><option value="30">30 days</option><option value="90">90 days</option></select>
                      </label>
                      <div className="invite-edit-actions">
                        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</button>
                        <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div><strong>{item.label}</strong><small>Created {relativeTime(item.created_at)}</small></div>
                      <div><strong>{item.redeemed_by_name || item.redeemed_email || 'Not redeemed'}</strong><small>{item.redeemed_email || `Expires ${relativeTime(item.expires_at)}`}</small></div>
                      <span className={`invite-status invite-status-${item.status}`}>{item.status === 'shared' ? 'copied' : item.status}</span>
                      <small>{item.redeemed_at ? `Redeemed ${relativeTime(item.redeemed_at)}` : item.opened_at ? `Opened ${relativeTime(item.opened_at)}` : item.shared_at ? `Copied ${relativeTime(item.shared_at)}` : 'Not copied yet'}</small>
                      {sendableInvitations.some((candidate) => candidate.id === item.id) && <div className="invite-actions"><button className="text-button" type="button" disabled={busy} onClick={() => handleShare(item)}>{copiedId === item.id ? 'Copied' : 'Copy link'}</button><button className="text-button" type="button" disabled={busy} onClick={() => setEditing({ id: item.id, label: item.label, expiresInDays: 30 })}>Edit</button><button className="text-button text-button-danger" type="button" disabled={busy} onClick={() => handleRevoke(item)}>Revoke</button></div>}
                    </>
                  )}
                </div>
              ))}
              {invitations.length === 0 && <p className="card-copy">No invitation codes yet.</p>}
            </div>
          </section>

          <section className="card admin-panel">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Users</h2><span>Last activity across sign-in, runs, and approvals</span></div>
              <span>{metrics.total_users || users.length} total</span>
            </div>
            <div className="admin-table-wrap">
              <div className="admin-table admin-table-head"><span>User</span><span>Channels</span><span>Last active</span><span>Runs</span><span>Applied</span></div>
              {users.map((item) => (
                <div key={item.id} className="admin-table admin-table-row">
                  <span><strong>{item.display_name || item.email}</strong><small>{item.email}</small></span>
                  <span className="admin-channels">
                    {userChannels(item).map((channel, index) => (
                      <span key={`${channel.provider}-${channel.ad_account_id || index}`}>
                        <strong>{channelLabel(channel.provider)}</strong>
                        <small>{channel.ad_account_id || 'Not connected'} · {channel.status || 'pending'}</small>
                      </span>
                    ))}
                  </span>
                  <span>{relativeTime(item.last_active_at)}</span>
                  <span>{item.agent_runs_7d}</span>
                  <span>{item.changes_applied_7d}</span>
                </div>
              ))}
              {users.length === 0 && <p className="card-copy">No AdPilot users yet.</p>}
            </div>
          </section>

          <section className="card admin-panel">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div><h2>Recent actions</h2><span>{metrics.failed_runs_7d || 0} failed · {metrics.skipped_runs_7d || 0} skipped in 7 days</span></div>
              <span>{actions.length} shown</span>
            </div>
            <div className="admin-actions">
              {actions.map((action) => (
                <div key={`${action.action_type}-${action.id}`} className="admin-action">
                  <span className={`status-dot status-${action.status}`} />
                  <div>
                    <strong>{action.label}</strong>
                    <small>{action.email} · {String(action.action_type).replaceAll('_', ' ')}</small>
                    {actionSuggestion(action) && <small className="admin-action-suggestion">{actionSuggestion(action)}</small>}
                  </div>
                  <span>{relativeTime(action.occurred_at)}</span>
                  <em>{action.status}</em>
                </div>
              ))}
              {actions.length === 0 && <p className="card-copy">No actions yet.</p>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default AdminPage;
